import {defineStore} from 'pinia';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import {Team, useTeamStore} from '@internalDevportal/store/TeamStore';
import ModulesService from '@core/services/ModulesService';
import {
  Options,
  SavedOptions,
  setNewOptions,
} from '@core/helpers/persistOptionsHelper';
import {AxiosResponse} from 'axios';
import {ApiProduct} from '@internalDevportal/store/Environment-ApiProductStore';
import moment from 'moment';
import {setApplicationOptions} from '@core/helpers/servicesHelper';
import {ref} from 'vue';
import ApiService from '@core/services/ApiService';
import ConfigProvider from '@core/services/ConfigProvider';
import snackbarService from '@core/services/SnackbarService';
import {getErrorMessage} from '@core/utils/getErrorMessage';

export interface Credential {
  id?: string;
  apiProducts: ApiProduct[];
  consumerKey: string;
  status: string;
  consumerSecret: string;
  expiresAt: string;
  issuedAt?: string;
}

interface NewApplication {
  name: string;
  apiProducts: string[];
  consumerKey: string | null;
  consumerSecret: string | null;
}

export interface Application {
  id: string;
  name?: string;
  appId: string;
  displayName: string;
  status: string;
  createdAt: string;
  role?: string;
  developer: {
    email: string;
  };
  environmentId?: string;
  company?: any;
  companyItAgreementId?: string;
  modules?: {
    AAD: {
      owned: boolean;
      redirectUris?: string[];
      keyCredentials: {
        keyId: string;
        endDatetime: string;
        thumbprint: string;
      }[];
    };
  };
  credentials: Credential[];
  productsCount?: number;
  teamName?: string;
  consumedByProduct?: boolean;
  isSelectable?: boolean;
  consumedByTeam?: boolean;
  sharedByYou?: boolean;
}

export const useApplicationStore = defineStore(
  'ApplicationStore',
  () => {
    let companyStorePromise: Promise<any> | null = null;
    let companyStore: any;

    if (ConfigProvider.value('subscriptionManagerModuleEnabled') !== 'false') {
      companyStorePromise =
        import('@subscriptionManager/store/CompanyStore').then(module => {
          companyStore = module.useCompanyStore();
          return companyStore;
        });
    }

    const api = ApiService.getApiInstance();
    const environmentStore = useEnvironmentStore();
    const teamStore = useTeamStore();

    const apps = ref<Application[]>([]);
    const applications = ref<Application[]>([]);
    const appsToShare = ref<Application[]>([]);
    const sharedApps = ref<Application[]>([]);
    const analyticsApps = ref<Application[]>([]);
    const applicationsWithoutCompany = ref<Application[]>([]);
    const totalApps = ref<number>(0);
    const tableApplications = ref<Application[]>([]);
    const currentApplication = ref<Application | undefined>(undefined);
    const loadingApplications = ref<boolean>(false);
    const loadingAppsForTeam = ref<boolean>(false);
    const loadingAppsWithoutCompany = ref<number>(0);
    const newOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const appCredentialsOptions = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const newOptionsApp = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {
        ids: undefined,
      },
    });
    const newOptionsAppsToShare = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {
        ids: undefined,
      },
    });
    const newOptionsSharedApps = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {
        ids: undefined,
      },
    });
    const newSearch = ref<string>('');
    const newSearchApp = ref<string>('');
    const newSearchAppsToShare = ref<string>('');
    const newSelectedCompany = ref<string>('');

    async function savePage(options: Options) {
      if (environmentStore.doesCurrentEnvSupportAppFiltering()) {
        await getApplications(options, newSearch.value);
      } else {
        newOptions.value = setNewOptions(options);
      }
    }

    async function saveSearch(value: string) {
      newSearch.value = value;
      newOptions.value.page = 1;
      if (environmentStore.doesCurrentEnvSupportAppFiltering()) {
        await getApplications(newOptions.value, newSearch.value);
      }
    }

    async function getAppsForTeam(team: Team | {}, options: Options) {
      loadingAppsForTeam.value = true;
      try {
        const baseOptions =
          options && Object.keys(options).length > 0
            ? options
            : newOptionsSharedApps.value;
        const sharedCount =
          team &&
          'sharedApplications' in team &&
          Array.isArray(team.sharedApplications)
            ? team.sharedApplications.length
            : 0;
        const usedOptions = {
          ...baseOptions,
          page: baseOptions.page ?? 1,
          itemsPerPage: baseOptions.itemsPerPage ?? Math.max(10, sharedCount),
        };
        if (!team || Object.keys(team).length === 0) {
          return;
        }
        if (!environmentStore?.currentEnvironment?.id) {
          return;
        }
        const teamId = 'id' in team ? team.id : '';
        const filterString = setApplicationOptions(usedOptions, '');
        const response = await api.get(
          `/environments/${environmentStore.currentEnvironment.id}/teams/${teamId}/applications${filterString}`,
        );
        if (response.data.data) {
          sharedApps.value = response.data.data;
          newOptionsSharedApps.value = setNewOptions({
            ...usedOptions,
            totalItems: response.data.page.total_elements,
          });
        }
      } finally {
        loadingAppsForTeam.value = false;
      }
    }

    async function getAppsForCompany(
      companyId: string | undefined,
      opts?: {skipCache?: boolean},
    ) {
      if (newOptionsApp.value.filter?.ids) {
        newOptionsApp.value.filter.ids = undefined;
      }
      const skipCache = opts?.skipCache === true;
      if (companyId && companyStorePromise) {
        try {
          if (!companyStore) await companyStorePromise;
          const foundCompany = await companyStore.value.getCompany(companyId);
          if (foundCompany?.apps) {
            if (environmentStore.doesCurrentEnvSupportAppFiltering()) {
              newOptionsApp.value.filter ||= {};
              newOptionsApp.value.filter.ids = foundCompany.apps.map(
                (a: {[x: string]: any}) => a['appId'],
              );
              await getApplicationsApps(
                newOptionsApp.value,
                undefined,
                skipCache,
              );
            } else {
              apps.value = foundCompany?.apps.filter(
                (app: Application) =>
                  app.environmentId === environmentStore.currentEnvironment?.id,
              );
            }
          } else {
            apps.value = [];
          }
        } catch (error) {
          snackbarService.showSadSnackbar(getErrorMessage(error));
          await getApplicationsApps(newOptionsApp.value, undefined);
        }
      } else {
        // Unscoped listing: use empty server search so persisted `newSearchApp` cannot shrink
        // the GET (AddApplications filters client-side via DataTable `search`).
        await getApplicationsApps(newOptionsApp.value, '', skipCache);
      }
    }

    async function editApplication({
      name,
      id,
    }: {
      name: string;
      id: string;
    }): Promise<AxiosResponse> {
      const envId = environmentStore?.currentEnvironment?.id;
      loadingApplications.value = true;
      try {
        const response = await api.put(
          `/environments/${envId}/applications/${id}`,
          {
            ...currentApplication.value,
            name,
          },
        );
        await getApplication(id);
        return response;
      } finally {
        loadingApplications.value = false;
      }
    }

    async function deleteApplication(applicationId: string) {
      const envId = environmentStore?.currentEnvironment?.id;
      await api.delete(`/environments/${envId}/applications/${applicationId}`);
      deleteApp(applicationId);
      await teamStore.loadTeams();
    }

    async function createApplication(
      application: NewApplication,
    ): Promise<Application> {
      loadingApplications.value = true;
      try {
        const response = await api.post(
          `/environments/${environmentStore?.currentEnvironment?.id}/applications`,
          application,
        );
        return response.data;
      } finally {
        loadingApplications.value = false;
      }
    }

    async function getApplication(
      id: string | string[],
    ): Promise<Application | undefined> {
      const resolvedId = Array.isArray(id) ? id[0] : id;
      if (!resolvedId) return;
      loadingApplications.value = true;
      if (!environmentStore?.currentEnvironment?.id) {
        loadingApplications.value = false;
        return;
      }
      try {
        const response = await api.get(
          `/environments/${environmentStore.currentEnvironment.id}/applications/${resolvedId}`,
        );
        const application = response.data;
        if (ModulesService.subscriptionManagerEnabled) {
          const companyResponse = await api.get(
            `/companyapplications?filter={"where": { "appId": "${resolvedId}" }, "include": ["company"]}`,
          );
          if (companyResponse.data.length > 0) {
            application.company = companyResponse.data[0].company;
            application.companyItAgreementId =
              companyResponse.data[0].itAgreementId;
          }
        }
        currentApplication.value = application;
        return response.data;
      } finally {
        loadingApplications.value = false;
      }
    }

    async function getApplications(
      options: Options | undefined,
      search: string,
      skipCache?: boolean,
    ) {
      const usedOptions = options || newOptions.value;
      const data = await getApplicationsData(usedOptions, search, skipCache);
      if (data) {
        applications.value = data.apps;
        newOptions.value = data.returnOptions;
      }
      loadingApplications.value = false;
    }

    async function getApplicationsApps(
      options: Options | undefined,
      search: string | undefined,
      skipCache?: boolean,
    ) {
      loadingApplications.value = true;
      const usedOptions = options || newOptionsApp.value;
      const usedSearch = search ?? newSearchApp.value;
      const data = await getApplicationsData(
        usedOptions,
        usedSearch,
        skipCache,
      );
      if (data) {
        apps.value = data.apps;
        newOptionsApp.value = data.returnOptions;
        newSearchApp.value = usedSearch;
      }
      loadingApplications.value = false;
    }

    async function getAppsToShare(
      options: Options | undefined,
      search: string,
    ) {
      loadingApplications.value = true;
      const usedOptions = options || newOptionsAppsToShare.value;
      const usedSearch = search || newSearchAppsToShare.value;
      const data = await getApplicationsData(usedOptions, usedSearch);
      if (data) {
        appsToShare.value = data.apps;
        newOptionsAppsToShare.value = data.returnOptions;
      }
      loadingApplications.value = false;
    }

    async function getAnalyticsApps() {
      const options: Options = {};
      if (environmentStore.doesCurrentEnvSupportAppFiltering()) {
        options.page = 1;
        options.itemsPerPage = 25;
      }
      const data = await getApplicationsData(options, '');
      if (data) {
        analyticsApps.value = data.apps;
      }
      loadingApplications.value = false;
    }

    async function getApplicationsData(
      options: Options,
      search: string,
      skipCache?: boolean,
    ): Promise<
      | {
          apps: Application[];
          returnOptions: SavedOptions;
        }
      | undefined
    > {
      loadingApplications.value = true;
      let returnOptions = {};
      let tempApps: Application[] = [];
      if (!environmentStore?.currentEnvironment?.id) {
        return;
      }
      let filterString = setApplicationOptions(options, search);
      if (skipCache) {
        filterString += '&skipCache=true';
      }
      const response = await api.get(
        `/environments/${environmentStore.currentEnvironment.id}/applications${filterString}`,
      );
      tempApps = response.data.data;
      returnOptions = setNewOptions({
        ...options,
        totalItems: response.data.page.total_elements,
      });
      return {
        apps: tempApps,
        returnOptions: returnOptions as SavedOptions,
      };
    }

    async function getApplicationsWithoutCompany(search: string) {
      if (!ModulesService.subscriptionManagerEnabled) {
        return;
      }
      if (!environmentStore?.currentEnvironment?.id) {
        return;
      }
      const options = `?page=1&itemsPerPage=20&search=${encodeURIComponent(search)}&company=false`;
      loadingAppsWithoutCompany.value += 1;
      const response = await api.get(
        `/environments/${environmentStore.currentEnvironment.id}/applications${options}`,
      );
      applicationsWithoutCompany.value = response.data.data;
      loadingAppsWithoutCompany.value -= 1;
    }

    function clearApplicationsWithoutCompany() {
      applicationsWithoutCompany.value = [];
    }

    function setCurrentApplication(appId: string) {
      currentApplication.value = applications.value.find(
        app => app.appId === appId,
      );
    }

    function deleteApp(applicationId: string) {
      applications.value = applications.value.filter(
        application => application.appId !== applicationId,
      );
    }

    function updateApplicationModule({
      module,
      moduleName,
    }: {
      module: any;
      moduleName: string;
    }) {
      if (currentApplication.value?.modules) {
        currentApplication.value.modules[
          moduleName as keyof typeof currentApplication.value.modules
        ] = module;
      }
    }

    async function updateExpiryDate(
      credential: Credential,
      date: Date | null,
      removeExpiryDate: boolean,
    ) {
      await api.post(
        `/environments/${environmentStore?.currentEnvironment?.id}/applications/${currentApplication?.value?.id}/keys/${credential.id}`,
        {
          expiryDate: date ? moment(date).format() : undefined,
          removeExpiryDate,
        },
      );
      if (currentApplication?.value?.id) {
        return getApplication(currentApplication?.value?.id);
      }
    }

    function $reset() {
      apps.value = [];
      applications.value = [];
      appsToShare.value = [];
      sharedApps.value = [];
      analyticsApps.value = [];
      applicationsWithoutCompany.value = [];
      totalApps.value = 0;
      tableApplications.value = [];
      currentApplication.value = undefined;
      loadingApplications.value = false;
      loadingAppsForTeam.value = false;
      loadingAppsWithoutCompany.value = 0;
      newOptions.value = {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      appCredentialsOptions.value = {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      newOptionsApp.value = {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {
          ids: undefined,
        },
      };
      newOptionsAppsToShare.value = {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {
          ids: undefined,
        },
      };
      newOptionsSharedApps.value = {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {
          ids: undefined,
        },
      };
      newSearch.value = '';
      newSearchApp.value = '';
      newSearchAppsToShare.value = '';
      newSelectedCompany.value = '';
    }

    return {
      apps,
      applications,
      appsToShare,
      sharedApps,
      analyticsApps,
      applicationsWithoutCompany,
      totalApps,
      tableApplications,
      currentApplication,
      loadingApplications,
      loadingAppsForTeam,
      loadingAppsWithoutCompany,
      newOptions,
      appCredentialsOptions,
      newOptionsApp,
      newOptionsAppsToShare,
      newOptionsSharedApps,
      newSearch,
      newSearchApp,
      newSearchAppsToShare,
      newSelectedCompany,
      savePage,
      saveSearch,
      getAppsForTeam,
      getAppsForCompany,
      editApplication,
      deleteApplication,
      createApplication,
      getApplication,
      getApplications,
      getApplicationsApps,
      getAppsToShare,
      getAnalyticsApps,
      getApplicationsData,
      getApplicationsWithoutCompany,
      clearApplicationsWithoutCompany,
      setCurrentApplication,
      deleteApp,
      updateApplicationModule,
      updateExpiryDate,
      $reset,
    };
  },
  {
    persist: {
      pick: [
        'newOptions',
        'newSearch',
        'newSearchApp',
        'appCredentialsOptions',
        'newSearchAppsToShare',
        'newOptionsApp',
        'newOptionsSharedApps',
        'newOptionsAppsToShare',
      ],
    },
  },
);
